import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, Trash2, UserCog, Truck, User, Pencil, Search, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const ROLE = {
  admin: { label: "Admin", icon: UserCog, cls: "bg-freight text-black" },
  driver: { label: "Conductor", icon: Truck, cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300" },
  client: { label: "Cliente", icon: User, cls: "bg-neutral-200 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300" },
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", phone: "", company: "", last_name: "", dni: "", plate: "", company_cif: "", company_address: "", company_city: "", company_phone: "" });
  const [editU, setEditU] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", role: "client", phone: "", company: "", password: "", last_name: "", dni: "", plate: "", company_cif: "", company_address: "", company_city: "", company_phone: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [companyNif, setCompanyNif] = useState({});
  const toggle = (co) => setExpanded((e) => ({ ...e, [co]: !e[co] }));
  const loadCompanies = () => api.get("/companies").then((r) => { const m = {}; r.data.forEach((c) => { m[c.company] = c.nif || ""; }); setCompanyNif(m); }).catch(() => {});
  const saveNif = async (co) => {
    try { await api.put("/companies/nif", { company: co, nif: companyNif[co] || "" }); toast.success("CIF de empresa guardado"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEdit = (u) => { setEf({ name: u.name || "", email: u.email || "", role: u.role, phone: u.phone || "", company: u.company || "", password: "", last_name: u.last_name || "", dni: u.dni || "", plate: u.plate || "", company_cif: u.company_cif || "", company_address: u.company_address || "", company_city: u.company_city || "", company_phone: u.company_phone || "" }); setEditU(u); };
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = { name: ef.name, email: ef.email || null, role: ef.role, phone: ef.phone, company: ef.company, last_name: ef.last_name, dni: ef.dni, plate: ef.plate, company_cif: ef.company_cif, company_address: ef.company_address, company_city: ef.company_city, company_phone: ef.company_phone };
      if (ef.password) payload.password = ef.password;
      await api.put(`/users/${editU.id}`, payload);
      toast.success("Usuario actualizado");
      setEditU(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  const load = () => { setLoading(true); api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); loadCompanies(); }, []);

  const create = async () => {
    if (!form.name || !form.password) { toast.error("Completa nombre y contraseña"); return; }
    if (form.role !== "driver" && !form.email) { toast.error("El correo es obligatorio para este rol"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users", { ...form, email: form.email || null });
      if (form.role === "driver" && !form.email) toast.success(`Conductor creado. Acceso: ${data.email}`);
      else toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "client", phone: "", company: "", last_name: "", dni: "", plate: "", company_cif: "", company_address: "", company_city: "", company_phone: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight";

  const ql = q.toLowerCase();
  const matchU = (u) => !q ||
    (u.name || "").toLowerCase().includes(ql) ||
    (u.email || "").toLowerCase().includes(ql) ||
    (u.company || "").toLowerCase().includes(ql);
  const clientUsers = users.filter((u) => u.role === "client");
  const internalUsers = users.filter((u) => u.role !== "client" && matchU(u));
  const groupsMap = {};
  clientUsers.forEach((u) => { const co = (u.company || "").trim() || "Sin empresa"; (groupsMap[co] = groupsMap[co] || []).push(u); });
  const companyGroups = Object.entries(groupsMap)
    .map(([co, ops]) => { const cm = !q || co.toLowerCase().includes(ql); return { co, ops, list: cm ? ops : ops.filter(matchU) }; })
    .filter((g) => g.list.length > 0)
    .sort((a, b) => a.co.localeCompare(b.co));

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader
          title="Usuarios"
          subtitle={`${users.length} usuario(s)`}
          action={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="create-user-btn" className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors">
                  <Plus className="w-4 h-4" /> Nuevo usuario
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-card">
                <DialogHeader><DialogTitle className="font-display font-black uppercase">Nuevo Usuario</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <input data-testid="user-name" className={inputCls} placeholder="Nombre completo *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="user-email" type="email" className={inputCls} placeholder={form.role === "driver" ? "Correo (opcional para conductores)" : "Correo *"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input data-testid="user-password" type="text" className={inputCls} placeholder="Contraseña *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  <select data-testid="user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="client">Cliente</option>
                    <option value="driver">Conductor</option>
                    <option value="admin">Administrador</option>
                  </select>
                  {form.role === "driver" && !form.email && (
                    <p className="text-xs text-muted-foreground">Se generará un usuario de acceso automático para el conductor.</p>
                  )}
                  {form.role === "driver" && (
                    <div className="border-t border-border pt-3 space-y-3">
                      <p className="text-[10px] font-bold uppercase text-freight">Datos del conductor (albarán / DeCA)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="user-lastname" className={inputCls} placeholder="Apellidos" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                        <input data-testid="user-dni" className={inputCls} placeholder="DNI" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} />
                      </div>
                      <input data-testid="user-plate" className={inputCls} placeholder="Matrícula del vehículo" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
                      <p className="text-[10px] font-bold uppercase text-freight pt-1">Empresa transportista (datos para el DeCA)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="user-company-cif" className={inputCls} placeholder="CIF empresa" value={form.company_cif} onChange={(e) => setForm({ ...form, company_cif: e.target.value })} />
                        <input data-testid="user-company-phone" className={inputCls} placeholder="Teléfono empresa" value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} />
                      </div>
                      <input data-testid="user-company-address" className={inputCls} placeholder="Dirección empresa" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} />
                      <input data-testid="user-company-city" className={inputCls} placeholder="CP y ciudad" value={form.company_city} onChange={(e) => setForm({ ...form, company_city: e.target.value })} />
                      <p className="text-[10px] text-muted-foreground leading-tight">Escribe el nombre en el campo <b>Empresa</b> (abajo). Si no es Triple M, en el DeCA figurará como transportista efectivo y Triple M como cargador contractual.</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="user-phone" className={inputCls} placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <input data-testid="user-company" className={inputCls} placeholder="Empresa" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="save-user-btn" onClick={create} disabled={busy} className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>
        ) : (
          <div className="space-y-6">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="search-users" className="w-full bg-background border border-input rounded-none pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight"
                placeholder="Buscar por empresa, nombre o correo" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>

            <div>
              <h3 className="font-display text-lg font-black uppercase tracking-tight mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-corp" /> Clientes por empresa
                <span className="text-xs font-bold text-muted-foreground">({companyGroups.length} empresa(s))</span>
              </h3>
              {companyGroups.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">No hay clientes.</div>
              ) : (
                <div className="space-y-2.5">
                  {companyGroups.map(({ co, list }) => {
                    const isOpen = q ? true : !!expanded[co];
                    return (
                      <div key={co} data-testid={`company-group-${co}`} className="border border-border bg-card">
                        <button data-testid={`company-toggle-${co}`} onClick={() => toggle(co)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-freight/5 transition-colors">
                          <span className="flex items-center gap-2 font-bold uppercase text-sm">
                            <Building2 className="w-4 h-4 text-corp" /> {co}
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">{list.length} operativo(s)</span>
                          </span>
                          {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        </button>
                        {isOpen && (
                          <div className="border-t border-border">
                            {co !== "Sin empresa" && (
                              <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-b border-border">
                                <span className="text-[10px] font-bold uppercase text-muted-foreground whitespace-nowrap">CIF/NIF empresa</span>
                                <input data-testid={`company-nif-${co}`} className="flex-1 max-w-[220px] bg-background border border-input rounded-none px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-freight" placeholder="Ej: B12345678" value={companyNif[co] || ""} onChange={(e) => setCompanyNif({ ...companyNif, [co]: e.target.value })} />
                                <button data-testid={`save-company-nif-${co}`} onClick={() => saveNif(co)} className="text-corp hover:underline text-xs font-bold uppercase">Guardar CIF</button>
                              </div>
                            )}
                            <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                                  <th className="py-2.5 px-4 font-bold">Operativo</th>
                                  <th className="py-2.5 px-4 font-bold hidden lg:table-cell">Empresa</th>
                                  <th className="py-2.5 px-4 font-bold hidden md:table-cell">Correo</th>
                                  <th className="py-2.5 px-4 font-bold hidden sm:table-cell">Teléfono</th>
                                  <th className="py-2.5 px-4 font-bold text-right">Acción</th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.map((u) => (
                                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-0 hover:bg-freight/5 transition-colors">
                                    <td className="py-2.5 px-4 font-semibold flex items-center gap-2"><User className="w-3.5 h-3.5 text-muted-foreground" />{u.name}</td>
                                    <td className="py-2.5 px-4 hidden lg:table-cell"><span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-corp/10 text-corp"><Building2 className="w-3 h-3" />{u.company || "Sin empresa"}</span></td>
                                    <td className="py-2.5 px-4 hidden md:table-cell text-muted-foreground">{u.email}</td>
                                    <td className="py-2.5 px-4 hidden sm:table-cell text-muted-foreground">{u.phone || "—"}</td>
                                    <td className="py-2.5 px-4 text-right whitespace-nowrap">
                                      <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="text-corp hover:underline mr-4 inline-flex items-center gap-1 text-xs font-bold uppercase"><Pencil className="w-4 h-4" /> Editar</button>
                                      <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-red-500 hover:text-red-600 inline-flex align-middle"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {internalUsers.length > 0 && (
              <div>
                <h3 className="font-display text-lg font-black uppercase tracking-tight mb-3 flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-freight" /> Equipo interno
                  <span className="text-xs font-bold text-muted-foreground">({internalUsers.length})</span>
                </h3>
                <div className="border border-border overflow-x-auto bg-card">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="py-3 px-4 font-bold">Nombre</th>
                        <th className="py-3 px-4 font-bold hidden lg:table-cell">Empresa</th>
                        <th className="py-3 px-4 font-bold hidden md:table-cell">Correo</th>
                        <th className="py-3 px-4 font-bold">Rol</th>
                        <th className="py-3 px-4 font-bold text-right">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {internalUsers.map((u) => {
                        const r = ROLE[u.role] || ROLE.client;
                        const Icon = r.icon;
                        return (
                          <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-0 hover:bg-freight/5 transition-colors">
                            <td className="py-3 px-4 font-semibold">{u.name}</td>
                            <td className="py-3 px-4 hidden lg:table-cell text-muted-foreground">{u.company || "—"}</td>
                            <td className="py-3 px-4 hidden md:table-cell text-muted-foreground">{u.email}</td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase ${r.cls}`}><Icon className="w-3 h-3" />{r.label}</span>
                            </td>
                            <td className="py-3 px-4 text-right whitespace-nowrap">
                              <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="text-corp hover:underline mr-4 inline-flex items-center gap-1 text-xs font-bold uppercase"><Pencil className="w-4 h-4" /> Editar</button>
                              {u.id !== user.id && (
                                <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-red-500 hover:text-red-600 inline-flex align-middle"><Trash2 className="w-4 h-4" /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {editU && (
          <Dialog open={!!editU} onOpenChange={(o) => { if (!o) setEditU(null); }}>
            <DialogContent className="rounded-none bg-card">
              <DialogHeader><DialogTitle className="font-display font-black uppercase">Editar Usuario</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="edit-user-name" className={inputCls} placeholder="Nombre completo" value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} />
                <input data-testid="edit-user-email" className={inputCls} placeholder="Correo" value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} />
                <select data-testid="edit-user-role" className={inputCls} value={ef.role} onChange={(e) => setEf({ ...ef, role: e.target.value })}>
                  <option value="client">Cliente</option>
                  <option value="driver">Conductor</option>
                  <option value="admin">Administrador</option>
                </select>
                {ef.role === "driver" && (
                  <div className="border-t border-border pt-3 space-y-3">
                    <p className="text-[10px] font-bold uppercase text-freight">Datos del conductor (albarán / DeCA)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="edit-user-lastname" className={inputCls} placeholder="Apellidos" value={ef.last_name} onChange={(e) => setEf({ ...ef, last_name: e.target.value })} />
                      <input data-testid="edit-user-dni" className={inputCls} placeholder="DNI" value={ef.dni} onChange={(e) => setEf({ ...ef, dni: e.target.value })} />
                    </div>
                    <input data-testid="edit-user-plate" className={inputCls} placeholder="Matrícula del vehículo" value={ef.plate} onChange={(e) => setEf({ ...ef, plate: e.target.value })} />
                    <p className="text-[10px] font-bold uppercase text-freight pt-1">Empresa transportista (datos para el DeCA)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="edit-user-company-cif" className={inputCls} placeholder="CIF empresa" value={ef.company_cif} onChange={(e) => setEf({ ...ef, company_cif: e.target.value })} />
                      <input data-testid="edit-user-company-phone" className={inputCls} placeholder="Teléfono empresa" value={ef.company_phone} onChange={(e) => setEf({ ...ef, company_phone: e.target.value })} />
                    </div>
                    <input data-testid="edit-user-company-address" className={inputCls} placeholder="Dirección empresa" value={ef.company_address} onChange={(e) => setEf({ ...ef, company_address: e.target.value })} />
                    <input data-testid="edit-user-company-city" className={inputCls} placeholder="CP y ciudad" value={ef.company_city} onChange={(e) => setEf({ ...ef, company_city: e.target.value })} />
                    <p className="text-[10px] text-muted-foreground leading-tight">Nombre en el campo <b>Empresa</b> (abajo). Si no es Triple M, en el DeCA figurará como transportista efectivo y Triple M como cargador contractual.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="edit-user-phone" className={inputCls} placeholder="Teléfono" value={ef.phone} onChange={(e) => setEf({ ...ef, phone: e.target.value })} />
                  <input data-testid="edit-user-company" className={inputCls} placeholder="Empresa" value={ef.company} onChange={(e) => setEf({ ...ef, company: e.target.value })} />
                </div>
                <input data-testid="edit-user-password" type="text" className={inputCls} placeholder="Nueva contraseña (vacío = no cambiar)" value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} />
              </div>
              <DialogFooter>
                <button data-testid="save-edit-user-btn" onClick={saveEdit} disabled={editBusy} className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                  {editBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar cambios
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
